/* eslint-disable */

import { TGroupSyntaxTreeNumericExpression, TSyntaxTree, TSyntaxTreeBuilderContext, TSyntaxTreeInstruction, TSyntaxTreeList, TSyntaxTreePos, TSyntaxTreeProgram, TSyntaxTreeString } from "./ElfAst";

export type TElfListCycles = {
    memRegOnly: TElfMemRegPtrValuesCycles | false,
    cyclesPerBatch: number,
    batchSize: number
}

export type TElfStringCycles = {
    cyclesPerBatch: number,
    batchSize: number
}

export type TElfMemRegPtrValuesCycles = {
    refRegister: number;
    refMemory: number;
}

function calculateBatchRuntimeCycles(listLength: number, batchSettings: { cyclesPerBatch: number, batchSize: number }): number {
    return Math.max(1, Math.ceil(listLength / batchSettings.batchSize)) * batchSettings.cyclesPerBatch;
}



export class ElfRuntimeException extends Error {
    public type: 'ELF_RUNTIME_EXCEPTION'
    constructor(public message: string, public detailedMessage: string, public codeMap?: ElfCodeMap) {
        super(message);
        this.type = 'ELF_RUNTIME_EXCEPTION';
        this.message = message;
    }
}

export class ElfHeap {
    private memory: Float64Array;
    constructor(private size: number) {
        this.size = size;
        this.memory = new Float64Array(this.size);
    }
    clone() {
        const cloned = new ElfHeap(this.size);
        cloned.memory.set(this.memory);
        return cloned;
    }
    private assertAccess(offset: number, codeMap: ElfCodeMap): number {
        if (offset < 0 || offset >= this.size) {
            throw new ElfRuntimeException("Indeks dostopa do pomnilnika je izven dovoljenega območja!", `lokacija: "${offset}"`, codeMap);
        }
        return offset;
    }
    get(offset: number, codeMap: ElfCodeMap): number {
        if (!codeMap) throw new Error('THIS SHOULD NOT HAPPEN - COMPILER ERROR');
        return this.memory[this.assertAccess(Math.trunc(offset), codeMap)];
    }
    set(offset: number, value: number, codeMap: ElfCodeMap): number {
        if (!codeMap) throw new Error('THIS SHOULD NOT HAPPEN - COMPILER ERROR');
        return this.memory[this.assertAccess(Math.trunc(offset), codeMap)] = value;
    }
}

export class ElfStack {
    private index: number;
    private memory: Float64Array;
    constructor(private size: number) {
        this.index = 0;
        this.memory = new Float64Array(this.size);
    }
    clone() {
        const cloned = new ElfStack(this.size);
        cloned.memory.set(this.memory);
        cloned.index = this.index;
        return cloned;
    }
    push(number: number, codeMap: ElfCodeMap): number {
        if (!codeMap) throw new Error('THIS SHOULD NOT HAPPEN - COMPILER ERROR');
        if (this.index >= this.size) {
            throw new ElfRuntimeException("prekoračitev sklada!", "", codeMap);
        }
        return this.memory[this.index++] = number;
    }
    pop(codeMap: ElfCodeMap): number {
        if (!codeMap) throw new Error('THIS SHOULD NOT HAPPEN - COMPILER ERROR');
        if (this.index <= 0) {
            throw new ElfRuntimeException("podkoračitev sklada!", "", codeMap);
        }
        return this.memory[--this.index];
    }

    getSize(): number {
        return this.index;
    }
}

export class ElfRegisters {
    private memory: Float64Array;
    constructor(private count: number) {
        this.count = count;
        this.memory = new Float64Array(this.count + 1); // +1 for accumulator which always exists.
    }
    getIndexedCount() {
        return this.count;
    }
    clone() {
        const cloned = new ElfRegisters(this.count);
        cloned.memory.set(this.memory);
        return cloned;
    }
    private assertAccess(offset: number, codeMap: ElfCodeMap): number {
        if (offset < 0 || offset > this.count) {
            throw new ElfRuntimeException("Register ne obstaja!", `Na mestu "${offset}" ni registra`, codeMap);
        }
        return offset;
    }
    getAccumulator(): number {
        return this.memory[0];
    }
    setAccumulator(value: number): number {
        return this.memory[0] = value;
    }
    get(offset: number, codeMap: ElfCodeMap): number {
        if (!codeMap) throw new Error('THIS SHOULD NOT HAPPEN - COMPILER ERROR');
        return this.memory[this.assertAccess(offset, codeMap)];
    }
    set(offset: number, value: number, codeMap: ElfCodeMap): number {
        if (!codeMap) throw new Error('THIS SHOULD NOT HAPPEN - COMPILER ERROR');
        return this.memory[this.assertAccess(offset, codeMap)] = value;
    }
}

export class ElfCallStack {
    private index: number;
    private stack: Array<{ codeMap: ElfCodeMap, name: string }>;
    constructor(private size: number) {
        this.size = size;
        this.stack = new Array(this.size);
        this.index = 0;
    }
    clone() {
        const cloned = new ElfCallStack(this.size);
        cloned.stack = this.stack.slice(0);
        cloned.index = this.index;
        return cloned;
    }
    push(call: ElfCallStack['stack'][0]): void {
        if (this.index >= this.size) {
            throw new ElfRuntimeException(`funkcijska prekoračitev sklada!`, "", call.codeMap);
        }
        this.stack[this.index] = call;
        ++this.index;
    }
    pop(): void {
        if (this.index <= 0) {
            throw new Error('Internal compiler error, callstack underflow?!');
        }
        --this.index;
    }

    getTailOf(size: number): ElfCallStack['stack'] {
        if (this.index <= 0) return [];
        if (size <= this.index) return this.stack.slice(this.index - size, this.index);
        return this.stack.slice(0, this.index);
    }

    peekAt(index: number): ElfCallStack['stack'][0] {
        return this.stack[index];
    }

    getSize(): number {
        return this.index;
    }
}

export class ElfCpuCycles {
    private cyclesUsed: number;
    private currentCodeMap: ElfCodeMap;
    constructor(private availableCycles: number) {
        this.cyclesUsed = 0;
        this.currentCodeMap = { line: 0, column: 0 };
        this.availableCycles = availableCycles;
    }

    clone() {
        const cloned = new ElfCpuCycles(this.availableCycles);
        cloned.cyclesUsed = this.cyclesUsed;
        cloned.currentCodeMap = this.currentCodeMap;
        return cloned;
    }

    totalConsumed(): number {
        return this.cyclesUsed;
    }

    run(codeMap: ElfCodeMap, cycles: number): void {
        this.cyclesUsed += cycles;
        this.currentCodeMap = { ...codeMap };
        if (this.cyclesUsed > this.availableCycles) {
            throw new ElfRuntimeException(`Program je presegel maksimalno število ciklov`, "", codeMap);
        }
    }

    getCodeMapPointer(): ElfCodeMap {
        return { ...this.currentCodeMap };
    }
}

export class ElfRuntimeLib {
    public print = (...args: number[]): void => { };
    public printText = (...args: number[]): void => { };
    public prompt = (str: string): string => "";
    public debuggerPrompt = async (str: string): Promise<string> => Promise.resolve("");
    public debuggerOnStep = async (codeMap: ElfCodeMap, executableStepExtArg: ElfCodeMapExecutableStepExt | null, evalStep: ElfEvalStepValue | null, metadata: ElfStepMetadata | null): Promise<number> => 1;
}

export type ElfStepMetadata = { debugHere: true };
export type ElfEvalStepValue = { evaluated: Float64Array[0] | Array<number> /* array is only in case of list or string, which are only passed into instructions (no runtime calculation) */ }
export type ElfCodeMapExecutableStepExt = { execUid: string | null, endLine: number | null, endColumn: number | null };
export type ElfCodeMap = Pick<TSyntaxTree, 'line' | 'column'>;


type AsyncFunctionConstructor = new (...args: string[]) => (...args: any[]) => Promise<any>;

export class ElfJsRuntime {
    private runtime: Function;
    constructor(private code: string) {
        const AsyncFunction = async function () { }.constructor as AsyncFunctionConstructor;
        this.runtime = new AsyncFunction("elfBuildArgs", this.code)
    }
    public async run(
        args: { registers: ElfRegisters, heap: ElfHeap, stack: ElfStack, callStack: ElfCallStack, cpuCycles: ElfCpuCycles, runtimeLib: ElfRuntimeLib }
    ): Promise<{ exitCode: number, terminated: boolean, userTerminated: boolean, codeMap?: ElfCodeMap }> {
        return await this.runtime(args);
    }
}



const cmds = {
    'heap.get': (meta: { codeMap: ElfCodeMap, debug: boolean, astBuildContext: TSyntaxTreeBuilderContext }, expr: TSyntaxTree) => `heap.get(${buildExpression(expr, { debug: meta.debug, astBuildContext: meta.astBuildContext })}, ${buildCodeMap(meta.codeMap)})`,
    'heap.set<EXPR, CODE>': (meta: { codeMap: ElfCodeMap, debug: boolean, astBuildContext: TSyntaxTreeBuilderContext }, expr: TSyntaxTree, code: string) => `heap.set(${buildExpression(expr, { debug: meta.debug, astBuildContext: meta.astBuildContext })}, (${code}), ${buildCodeMap(meta.codeMap)})`,
    'heap.set<FN(OFFSET,VALUE)>': (meta: { codeMap: ElfCodeMap }) => `((offset,value)=>heap.set(offset, value, ${buildCodeMap(meta.codeMap)}))`,
    'registers.getAccumulator<INTERNAL>': () => `registers.getAccumulator()`,
    'registers.setAccumulator<INTERNAL>': (code: string) => `registers.setAccumulator(${code})`,
    'registers.get<NUMBER>': (meta: { codeMap: ElfCodeMap }, offset: number) => `registers.get(${offset}, ${buildCodeMap(meta.codeMap)})`,
    'registers.set<NUMBER,CODE>': (meta: { codeMap: ElfCodeMap }, offset: number, code: string) => `registers.set(${offset}, (${code}), ${buildCodeMap(meta.codeMap)})`,
    'registers.set<FN(OFFSET,VALUE)>': (meta: { codeMap: ElfCodeMap }) => `((offset,value)=>registers.set(offset, value, ${buildCodeMap(meta.codeMap)}))`,
    'stack.push': (meta: { codeMap: ElfCodeMap, debug: boolean, astBuildContext: TSyntaxTreeBuilderContext }, expr: TSyntaxTree) => `stack.push(${buildExpression(expr, { debug: meta.debug, astBuildContext: meta.astBuildContext })}, ${buildCodeMap(meta.codeMap)})`,
    'stack.push<CODE>': (meta: { codeMap: ElfCodeMap }, code: string) => `stack.push(${code}, ${buildCodeMap(meta.codeMap)})`,
    'stack.pop': (meta: { codeMap: ElfCodeMap }) => `stack.pop(${buildCodeMap(meta.codeMap)})`,
    'stack.getSize': () => `stack.getSize()`,
    'cpuCycles.run': (meta: { codeMap: ElfCodeMap }, cycles: number) => `cpuCycles.run(${buildCodeMap(meta.codeMap)}, ${cycles})`,
    'cpuCycles.run<PROXY>': (meta: { codeMap: string }, code: string) => `cpuCycles.run(${meta.codeMap}, ${code})`,
    'cpuCycles.run<INSTRUCTION_BATCH>': (meta: { codeMap: ElfCodeMap }, instruction: { baseCycles: number, batchSize: number, listSizeCode: string }) => `cpuCycles.run(${buildCodeMap(meta.codeMap)}, Math.max(1, Math.ceil(${instruction.listSizeCode} / ${instruction.batchSize})) * ${instruction.baseCycles})`,
    'callStack.pop<INTERNAL>': () => `callStack.pop()`,
    'callStack.push<INTERNAL>': (meta: { codeMap: string }, functionName: string) => `callStack.push({ codeMap: ${meta.codeMap}, name: "${functionName}" })`
} as const;

export function compileExe<T extends keyof typeof cmds>(name: T, ...args: Parameters<(typeof cmds)[T]>) {
    return `${(cmds[name] as any)(...args)}`
}

export function buildCodeMap(codeMap: ElfCodeMap | TSyntaxTree): string {
    return `{ line: ${codeMap.line}, column: ${codeMap.column} }`
}


function buildDebuggerStep(tree: TSyntaxTree, opts: { evaluated: boolean, forceInstruction?: boolean }): string {
    const codeMap = buildCodeMap(tree);
    const optInstruction = buildExecutableDebuggerStepMap(tree, opts.forceInstruction);
    const stepOpts = opts.evaluated ? `{ evaluated }` : "null";
    const metadata = tree.type === 'DEBUGPOINT' ? '{ debugHere: true }' : 'null'
    return `if((await runtimeLib.debuggerOnStep(${codeMap},${optInstruction},${stepOpts},${metadata})) === -1) throw new ElfTerminateError("TERMINATED", -1, true, ${buildCodeMap(tree)})`;
}

export function injectWrapperDebuggerBefore(tree: TSyntaxTree, opts?: { forceInstruction?: boolean }) {
    return `(await (async ()=>{ ${buildDebuggerStep(tree, { evaluated: false, forceInstruction: opts?.forceInstruction })}; })())`
}

export function injectWrapperDebuggerAfter(tree: TSyntaxTree, opts?: { forceInstruction?: boolean }) {
    return `(async (evaluated)=>{ ${buildDebuggerStep(tree, { evaluated: true, forceInstruction: opts?.forceInstruction })}; return evaluated; })`
}

// used for { continue, break, return, throw} where we want to still debug after the statement executes, but we delay actual execution as we cant put code listener after it.
export function buildEagerInlineWrapper(args: { cycles: number, tree: TSyntaxTree, debug: boolean }, expressionCode: string, runTransformer: (code: string) => string): string {
    if (args.debug) {
        const injectedDebuggerExprCode = `(await ${injectWrapperDebuggerAfter(args.tree)}(${expressionCode}))`;
        const exec = runTransformer(injectedDebuggerExprCode);
        return `\n${injectWrapperDebuggerBefore(args.tree)};${compileExe('cpuCycles.run', { codeMap: args.tree }, args.cycles)};\n${exec};`;
    } else {
        const injectedExprCode = `(${expressionCode})`;
        const exec = runTransformer(injectedExprCode);
        return `\n${compileExe('cpuCycles.run', { codeMap: args.tree }, args.cycles)};\n${exec};`;
    }
}

// wraps multiple statements in transpiled language - so its a clear chunk of translation from elf -> js
export function buildWrapper(args: { cycles: number, tree: TSyntaxTree, debug: boolean, forceInstruction?: boolean }, code: string): string {
    const statCode = `{ cycles: ${args.cycles}, codeMap: ${buildCodeMap(args.tree)} }`;
    if (args.debug) {
        return `(await WRAPPER(${statCode},${injectWrapperDebuggerBefore(args.tree, { forceInstruction: args.forceInstruction })},${injectWrapperDebuggerAfter(args.tree, { forceInstruction: args.forceInstruction })},(${code})))`;
    } else {
        return `(WRAPPER(${statCode},(${code})))`;
    }
}

function buildInternalExecutionWrapper(args: { cycles: number, tree: TSyntaxTree, debug: boolean }, evalCode: string, internalCode: string) {
    const codeMap = buildCodeMap(args.tree)
    const statCode = `{ cycles: ${args.cycles}, codeMap: ${codeMap} }`;
    if (args.debug) {
        return `(await WRAPPER_INTERNAL(${statCode},${injectWrapperDebuggerBefore(args.tree)},${injectWrapperDebuggerAfter(args.tree)},${evalCode},${internalCode}))`;
    } else {
        return `(WRAPPER_INTERNAL(${statCode},${evalCode},${internalCode}))`;
    }
}

export function buildGenericInstructionWrapper(args: { cycles: number, tree: TSyntaxTree, debug: boolean }, parseInstructionArgs: (args: TSyntaxTree[]) => string, internalCodeFnBody: string) {
    const { debug, tree } = args;
    if (tree.type !== 'INSTRUCTION') throw new Error('COMPILER ERROR, expected instruction');
    const mappedArgs = parseInstructionArgs(tree.args);
    // internalCodeFnBody can never **EVER** perform async operation in RELEASE mode, as runtime will never be async and internal code can only happen sync!
    // currently only PROMPT "breaks" this rule, which returns promise BUT only in debug mode.
    const internalCodeFn = `${debug ? 'async ' : ''}(args)=>{ ${internalCodeFnBody}; return ${compileExe('registers.getAccumulator<INTERNAL>')}; }`;
    return buildInternalExecutionWrapper({ cycles: args.cycles, tree: args.tree, debug }, mappedArgs, internalCodeFn);
}



function buildGenericInstructionSetRegisterOrMemoryExprArg(args: { debug: boolean, astBuildContext: TSyntaxTreeBuilderContext, cyclesMemReg: TElfMemRegPtrValuesCycles }, regOrMemTreeArg: TSyntaxTree): string {
    const { debug, astBuildContext } = args;
    if (regOrMemTreeArg.type === 'REGISTER') {
        return buildWrapper({ cycles: args.cyclesMemReg.refRegister, tree: regOrMemTreeArg, debug }, `([0,${regOrMemTreeArg.registerNumber}])`);
    }
    if (regOrMemTreeArg.type === 'MEMORY') {
        return buildWrapper({ cycles: args.cyclesMemReg.refMemory, tree: regOrMemTreeArg, debug }, `([1,${buildExpression(regOrMemTreeArg.innerExpression, { debug, astBuildContext })}])`);
    }
    throw new Error('COMPILE ERROR: expected register or memory!');
}


export function buildGenericInstructionListArg(args: { debug: boolean, cyclesList: TElfListCycles, astBuildContext: TSyntaxTreeBuilderContext }, arg: TSyntaxTreeList) {
    const { cyclesList } = args;
    const cyclesUsed = calculateBatchRuntimeCycles(arg.list.length, cyclesList);
    const cyclesMemReg = cyclesList.memRegOnly;
    if (cyclesMemReg) {
        return `${buildWrapper({ cycles: cyclesUsed, tree: arg, debug: args.debug }, `[${arg.list.map(el => `${buildGenericInstructionSetRegisterOrMemoryExprArg({ ...args, cyclesMemReg }, el)}`).join(',')}]`)}`
    }
    return `${buildWrapper({ cycles: cyclesUsed, tree: arg, debug: args.debug }, `[${arg.list.map(el => buildExpression(el, { debug: args.debug, astBuildContext: args.astBuildContext })).join(',')}]`)}`
}

export function buildGenericInstructionStringArg(args: { debug: boolean, cyclesString: TElfStringCycles }, arg: TSyntaxTreeString) {
    const { cyclesString } = args;
    const argList = arg.value.split('');
    const cyclesUsed = calculateBatchRuntimeCycles(argList.length, cyclesString);
    return `${buildWrapper({ cycles: cyclesUsed, tree: arg, debug: args.debug }, `[${argList.map(el => el.charCodeAt(0)).join(',')}]`)}`
}



export function buildGenericInstructionExpressionArg(args: { debug: boolean, memRegOnly: TElfMemRegPtrValuesCycles | false, astBuildContext: TSyntaxTreeBuilderContext }, arg: TSyntaxTree) {
    if (args.memRegOnly) {
        return `${buildGenericInstructionSetRegisterOrMemoryExprArg({ ...args, cyclesMemReg: args.memRegOnly }, arg)}`;
    }
    return `${buildExpression(arg, { debug: args.debug, astBuildContext: args.astBuildContext })}`;
}


export function buildGenericNumericAccumulatorInstructionWrapper(args: { cycles: number, tree: TSyntaxTreeInstruction, astBuildContext: TSyntaxTreeBuilderContext, debug: boolean }, exprBuilder: (...numExpressions: string[]) => string) {
    const { debug, tree, cycles, astBuildContext } = args;
    return buildWrapper({ cycles, tree, debug }, compileExe('registers.setAccumulator<INTERNAL>', exprBuilder(...tree.args.map(arg => `Number(${buildExpression(arg, { debug, astBuildContext })})`))));
}

export function buildProgram(program: TSyntaxTreeProgram, args: { debug: boolean, astBuildContext: TSyntaxTreeBuilderContext }): string {
    let transpiled = "";
    const { debug, astBuildContext } = args;
    for (const seq of program.sequence) {
        const keyword = astBuildContext.languageContext.keywords[seq.type as keyof typeof astBuildContext.languageContext.keywords];
        if (keyword) {
            transpiled += keyword.transform(seq, args);
        } else {
            transpiled += `\n${buildExpression(seq, { debug, astBuildContext })};\n`
        }
    }
    return `${transpiled}\n`;
}



export function buildExpression(exprTree: TSyntaxTree, opts: { debug: boolean, astBuildContext: TSyntaxTreeBuilderContext, isParentControlStmt?: boolean }): string {
    if (exprTree.type === 'INSTRUCTION') {
        const instruction = opts.astBuildContext.languageContext.instructions[exprTree.instructionName];
        if (!instruction) {
            throw new Error('COMPILE ERROR, INVALID INSTRUCTION');
        }
        return instruction.transform(exprTree, opts);
    }
    const primitive = opts.astBuildContext.languageContext.primitives[exprTree.type as keyof typeof opts.astBuildContext.languageContext.primitives];
    if (!primitive) {
        throw new Error('COMPILE ERROR, INVALID PRIMITIVE');
    }
    return primitive.transform(exprTree as TGroupSyntaxTreeNumericExpression, opts);
}

// Debugger information builder.
function buildExecutableDebuggerStepMap(tree: TSyntaxTree, forceInstruction?: boolean): string {
    const useDefaultTokenizerPos = "null"; // default return "null" can be replaced with endLineColumn when tsyntax tree implements all endlines.
    const buildPayload = (asInstructionStep: boolean, tree: TSyntaxTree & { endPos: TSyntaxTreePos }) => {
        const endLine = ('eof' in tree.endPos) ? "null" : tree.endPos.line;
        const endColumn = ('eof' in tree.endPos) ? "null" : tree.endPos.column;
        const execUid = asInstructionStep ? `"${tree.line}_${tree.column}"` : 'null';
        return `{ execUid: ${execUid}, endLine: ${endLine}, endColumn: ${endColumn} }`
    }
    switch (tree.type) {
        // primitive types can only conditionally be marked as exec instructions (if [expr], while[expr])
        case 'LABEL':
        case 'NUMBER':
        case 'REGISTER':
        case 'MEMORY':
            return buildPayload(forceInstruction ?? false, tree);
        // list and string should not be marked as exec instruction, they are ALWAYS part of arguments to actual instruction.
        case 'LIST':
        case 'STRING':
            if ('endPos' in tree) {
                return buildPayload(false, tree);
            } else {
                return useDefaultTokenizerPos;
            }
        // everything else should be handled as instruction (ofc if they have endPos defined!).
        default:
            if ('endPos' in tree) {
                return buildPayload(true, tree);
            } else {
                // there are some uncovered instructions for those we sadly must fall back to simple instruction
                return useDefaultTokenizerPos;
            }
    }
}

export function compileRuntime(ast: TSyntaxTree, astBuildContext: TSyntaxTreeBuilderContext, options: { debug: boolean }): ElfJsRuntime {
    if (ast.type !== 'PROGRAM' || ast.blockOf !== 'ROOT') {
        throw new Error('INTERNAL ERROR, CAN ONLY RUN ROOT PROGRAMS!');
    }
    const { debug } = options;
    const transpiledHeader = `
// INIT BASE
const {heap, stack, callStack, registers, cpuCycles, runtimeLib} = elfBuildArgs;

class ElfTerminateError extends Error {
    constructor(message, code, userTerminated, codeMap) {
        super(message);
        this.code = code;
        this.userTerminated = userTerminated;
        this.codeMap = codeMap;
    }
}
${debug ? `
async function WRAPPER(metadata,...args) {
    ${compileExe('cpuCycles.run<PROXY>', { codeMap: 'metadata.codeMap' }, 'metadata.cycles')}
    const evaluated = args.at(-1);
    const debugHookAfter = args.at(-2);
    await debugHookAfter(evaluated);
    return evaluated;
}
async function WRAPPER_LAZY(metadata, ...args) {
    ${compileExe('cpuCycles.run<PROXY>', { codeMap: 'metadata.codeMap' }, 'metadata.cycles')}
    const debugHookAfter = args.at(-2);
    const evaluated = await args.at(-1)();
    await debugHookAfter(evaluated);
    return evaluated;
}
async function WRAPPER_INTERNAL(metadata, ...args) {
    ${compileExe('cpuCycles.run<PROXY>', { codeMap: 'metadata.codeMap' }, 'metadata.cycles')}
    const debugHookAfter = args.at(-3);
    const evalArgs = args.at(-2);
    const internalFn = args.at(-1);
    const evaluated = await internalFn(evalArgs);
    await debugHookAfter(evaluated);
    return evaluated;
}       
` : `
function WRAPPER(metadata,...args) {
    ${compileExe('cpuCycles.run<PROXY>', { codeMap: 'metadata.codeMap' }, 'metadata.cycles')}
    return args.at(-1);
}
function WRAPPER_LAZY(metadata, ...args) {
    ${compileExe('cpuCycles.run<PROXY>', { codeMap: 'metadata.codeMap' }, 'metadata.cycles')}
    return args.at(-1)();
}
function WRAPPER_INTERNAL(metadata, ...args) {
    ${compileExe('cpuCycles.run<PROXY>', { codeMap: 'metadata.codeMap' }, 'metadata.cycles')}
    const evalArgs = args.at(-2);
    const internalFn = args.at(-1);
    return internalFn(evalArgs);
}     
`}


// BEGIN TRANSPILED CODE\n\n`;

    let transpiled = ``;

    transpiled += `\n${buildProgram(ast, { debug, astBuildContext })};\n`

    const finalCode = `
        ${transpiledHeader}
        try {
            async function main() {
                ${transpiled}
            }
            await main();
            return { exitCode: 0, terminated: false, userTerminated: false, codeMap: null };
        } catch(exc) {
            if(exc instanceof ElfTerminateError) {
                return { exitCode: exc.code, terminated: true, userTerminated: exc.userTerminated, codeMap: exc.codeMap };
            }
            throw exc;
        }
    `;
    return new ElfJsRuntime(finalCode);
}
